import { useCallback, useEffect, useMemo, useRef } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash-es/cloneDeep';
import debounce from 'lodash-es/debounce';
import { BookIcon, CircleAlertIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { toast } from 'sonner';
import * as yup from 'yup';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  FormAutocomplete,
  AutocompleteOption,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import useDialogAlert from '@/hooks/useDialogAlert';
import useFocusOnInput from '@/hooks/useFocusOnInput';
import i18n from '@/i18n/i18n';
import { selectNodeIdByActionId, selectSelectedAction } from '@/redux/dialogs/selectors';
import { updateAction } from '@/redux/nodes/actions';
import { Action } from '@/shared/types/action';
import { RootState } from '@/shared/types/state';
import { GOOGLE_SHEET_DOCS_URL } from '@/util/constants';
import { addDollarSign, capitalizeFirstLetter } from '@/util/util';
import { errorMessage } from '@/util/validation/helpers';
import { spreadsheetIdPattern } from '@/util/validation/patterns';
import { contextVariableValueSchema } from '@/util/validation/schema-builders';

import DraggableItem from './DraggableItem';
import { useGoogleSheet } from './useGoogleSheet';
import ToolkitWrapper from '../../ToolkitWrapper';

import styles from './ToolkitGoogleSheet.module.scss';

// Schema for FormAutocomplete - validates string input value directly
const comboboxInputSchema = yup.object().shape({
  spreadsheet_id: yup
    .string()
    .max(64)
    .matches(spreadsheetIdPattern, {
      message: () => i18n.t('actions.googlesheet.regex_error_spreadsheet_id'),
    })
    .required(),
  sheet_id: yup.string().max(64).required(),
  input: contextVariableValueSchema,
});

type GoogleSheetFormValues = yup.InferType<typeof comboboxInputSchema>;

const MIN_COLUMNS = 1;
const MAX_COLUMNS = 15;

// Debounce the onSave function to prevent multiple calls
const useDebouncedSave = (saveFn: () => void, delay: number) => {
  const debouncedSaveRef = useRef(debounce(saveFn, delay));

  const debouncedSave = useCallback(() => {
    debouncedSaveRef.current();
  }, []);

  return debouncedSave;
};

// Handle onKeyDown event for debounced onSave function
const useHandleChange = (debouncedOnSave: () => void) => {
  return useCallback(() => {
    debouncedOnSave();
  }, [debouncedOnSave]);
};

const ToolkitGoogleSheet = () => {
  // Custom hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { contextVariables: autoCompleteOptions } = useBrainVariables();
  const { updateReduxAction } = useGoogleSheet();

  // Redux selectors
  const { type, actionId, sheet_id, spreadsheet_id, inputs } = useSelector((state: RootState) => {
    const selectedAction = selectSelectedAction(state) as Extract<Action, { type: 'googlesheet' }>;
    const { action_id, type, spreadsheet_id, sheet_id, inputs } = selectedAction;

    return {
      type,
      actionId: action_id,
      spreadsheet_id: spreadsheet_id ?? '',
      sheet_id: sheet_id ?? '',
      inputs: inputs || [],
    };
  }, shallowEqual);
  const parentNodeId = useSelector(selectNodeIdByActionId(actionId));

  // RHF
  const formMethods = useForm<GoogleSheetFormValues>({
    mode: 'onChange',
    defaultValues: { spreadsheet_id: spreadsheet_id, sheet_id: sheet_id, input: '' },
    resolver: yupResolver<GoogleSheetFormValues, unknown, GoogleSheetFormValues>(
      comboboxInputSchema
    ),
  });
  const {
    formState: { errors },
    getValues,
    control,
    trigger,
    resetField,
    setFocus,
    setValue,
  } = formMethods;
  useFocusOnInput('spreadsheet_id', errors, setFocus);
  const spreadsheetIdErrorMessage = errors?.spreadsheet_id?.message;
  const sheetIdErrorMessage = errors?.sheet_id?.message;
  const inputErrorMessage = errors?.input?.message;

  // Callbacks
  const handleDragItem = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      if (dragIndex === hoverIndex) {
        return inputs;
      }

      const draggedItem = inputs[dragIndex];
      const newList = cloneDeep(inputs);
      newList.splice(dragIndex, 1);
      newList.splice(hoverIndex, 0, draggedItem);
      updateReduxAction(actionId, 'inputs', newList);

      return inputs;
    },
    [actionId, inputs, updateReduxAction]
  );

  const handleDropItem = useCallback(() => {
    updateReduxAction(actionId, 'inputs', inputs);
  }, [actionId, inputs, updateReduxAction]);

  const handleDeleteItem = useCallback(
    (id: string) => {
      const newList = inputs.filter((item: string) => item !== id);
      updateReduxAction(actionId, 'inputs', newList);
    },
    [actionId, inputs, updateReduxAction]
  );

  const getIndex = useCallback(
    (item: string) => () => {
      return inputs.indexOf(item);
    },
    [inputs]
  );

  const { setFieldError } = useDialogAlert({
    id: actionId,
    nodeId: parentNodeId,
    title: t('actions.types.googlesheet'),
    type,
  });

  // Transform variables to FormAutocomplete options
  const comboboxOptions: AutocompleteOption[] = useMemo(() => {
    return autoCompleteOptions.map((option) => ({
      value: option.value,
      label: option.label,
      category: t(`auto_complete.group_labels.${option.type}`),
    }));
  }, [autoCompleteOptions, t]);

  const handleComboboxChange = useCallback(
    async (selectedValue: string) => {
      if (!selectedValue) {
        return;
      }

      // Display toast if the number of columns is the maximum
      if (inputs.length === MAX_COLUMNS) {
        resetField('input');
        toast.error(t('dialog.google.max_columns', { 0: MAX_COLUMNS }));
        return;
      }

      // Display toast warning when reaching max-1 columns
      if (inputs.length === MAX_COLUMNS - 1) {
        toast.warning(t('dialog.google.max_columns', { 0: MAX_COLUMNS }));
      }

      const selectedVariable = addDollarSign(selectedValue);

      // Set value before validation
      setValue('input', selectedVariable);

      const isValid = await trigger('input');
      // Show error message if the input is not valid
      if (!isValid) {
        return;
      }

      resetField('input');
      const isDuplicate = inputs.includes(selectedVariable);

      if (isDuplicate) {
        toast.warning(t('dialog.google.duplicate_variable'));
        return;
      }

      dispatch(updateAction({ actionId, action: { inputs: [...inputs, selectedVariable] } }));
    },
    [actionId, dispatch, inputs, resetField, setValue, t, trigger]
  );

  const handleCreateNew = useCallback(
    async (newValue: string) => {
      // The value from FormAutocomplete is passed through onChange which calls handleComboboxChange
      // This callback is called after onChange, so the value is already processed
      // We just need to handle it the same way
      await handleComboboxChange(newValue);
    },
    [handleComboboxChange]
  );

  useEffect(() => {
    (async () => {
      await trigger();
    })();
  }, [trigger]);

  useEffect(() => {
    setFieldError('spreadsheet_id', spreadsheetIdErrorMessage);
  }, [spreadsheetIdErrorMessage, setFieldError]);

  useEffect(() => {
    setFieldError('sheet_id', sheetIdErrorMessage);
  }, [sheetIdErrorMessage, setFieldError]);

  // Surface the "at least one column" error in the dialog-alerts panel, driven by the
  // Redux state rather than the UI. The UI prevents deleting the last column, but the
  // state is the source of truth and gates saving — consistent with other toolkit actions.
  useEffect(() => {
    const body =
      inputs.length < MIN_COLUMNS
        ? t('dialog.errors.at_least_one', { 0: t('actions.googlesheet.column') })
        : undefined;

    setFieldError('inputs', body);
  }, [inputs.length, t, setFieldError]);

  const saveAllVariablesInRedux = useCallback(() => {
    const { spreadsheet_id, sheet_id } = getValues();

    dispatch(updateAction({ actionId, action: { spreadsheet_id, sheet_id } }));
  }, [actionId, dispatch, getValues]);

  // Debounce the onSave function to prevent multiple calls
  const debouncedOnSave = useDebouncedSave(saveAllVariablesInRedux, 500);
  const handleDebouncedChange = useHandleChange(debouncedOnSave);

  return (
    <ToolkitWrapper type={type}>
      <ContextualHelp
        title={t('dialog.google.contextual_title')}
        name={type}
        preventClose
        links={[
          { label: t('docs.docs'), url: GOOGLE_SHEET_DOCS_URL, icon: <BookIcon size={16} /> },
        ]}
      >
        {t('dialog.google.contextual_message')}
      </ContextualHelp>

      <Form {...formMethods}>
        <FormField
          control={control}
          name="spreadsheet_id"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel>{t('dialog.google.spreadsheet_id')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    handleDebouncedChange();
                  }}
                />
              </FormControl>
              <FormMessage>
                {capitalizeFirstLetter(errorMessage({ field: errors.spreadsheet_id }))}
              </FormMessage>
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="sheet_id"
          render={({ field }) => (
            <FormItem className="mb-6">
              <FormLabel>{t('dialog.google.google_sheet_name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(event) => {
                    field.onChange(event);
                    handleDebouncedChange();
                  }}
                />
              </FormControl>
              <FormMessage>
                {capitalizeFirstLetter(errorMessage({ field: errors.sheet_id }))}
              </FormMessage>
            </FormItem>
          )}
        />
      </Form>

      <FormAutocomplete
        options={comboboxOptions}
        control={control}
        name="input"
        label={t('dialog.google.add_columns')}
        placeholder={t('dialog.google.inputs_placeholder')}
        onCreateNew={handleCreateNew}
        onChange={handleComboboxChange}
        error={!!errors?.input}
        errorMessage={capitalizeFirstLetter(inputErrorMessage)}
      />

      <ul className={styles.container}>
        {inputs.map((item, i: number) => {
          return (
            <DraggableItem
              key={item}
              id={item}
              index={i}
              item={item}
              moveItem={handleDragItem}
              dropItem={handleDropItem}
              deleteItem={handleDeleteItem}
              getIndex={getIndex(item)}
              disableClose={inputs.length === 1}
            />
          );
        })}
      </ul>

      {/* Restriction banners */}
      {inputs.length === MAX_COLUMNS && (
        <div className={styles.warning}>
          <Alert variant="warning">
            <CircleAlertIcon size={16} />
            <AlertDescription>
              {t('dialog.google.max_columns', { 0: MAX_COLUMNS })}
            </AlertDescription>
          </Alert>
        </div>
      )}
    </ToolkitWrapper>
  );
};

export default ToolkitGoogleSheet;
